import React, { useState, useMemo } from "react";
import { IconWrapper, SortIcon } from "common/components/icon";
import { useTranslation } from "react-i18next";
import {
  Grid,
  Th,
  ThLabel,
  Td,
  AdditionalTh,
  AdditionalTd,
  Divider,
  TdContent,
  EmptyRow,
} from "./ListStyles";
import { I18N_KEYS } from "common/i18n/keys";
import { colors } from "common/const/colors";

export const ListRenderer = ({
  columns,
  data,
  renderProps,
  hasPrefix,
  hasSuffix,
  prefixHeader,
  prefixBodyBuilder,
  suffixHeader,
  suffixBodyBuilder,
  onRowClick,
  sortKey,
  isAscending,
  onSort,
}) => {
  const { t } = useTranslation();
  const [hoveredRow, setHoveredRow] = useState(null);

  const gridColumns = `${hasPrefix ? "auto " : ""}${columns?.map((col) => `${col.ratio || 1}fr`).join(" ")}${hasSuffix ? " auto" : ""}`;
  const totalCols =
    (hasPrefix ? 1 : 0) + (columns?.length || 0) + (hasSuffix ? 1 : 0);

  // 1) mergeInfo 먼저 선언
  const mergeInfo = useMemo(() => {
    if (!data || data.length === 0) return [];

    const hasAnyMerge = columns?.some((col) => col.mergeBy);
    if (!hasAnyMerge) return [];

    const info = Array.from({ length: data.length }, () => ({}));

    columns.forEach((col) => {
      if (!col.mergeBy) {
        data.forEach((_, rowIdx) => {
          info[rowIdx][col.key] = { rowSpan: 1, skip: false };
        });
        return;
      }

      let prevKey = null;
      let startIdx = 0;

      data.forEach((row, rowIdx) => {
        const currentKey = col.mergeBy(row);

        if (rowIdx !== 0 && prevKey === currentKey) {
          info[startIdx][col.key].rowSpan += 1;
          info[rowIdx][col.key] = { rowSpan: 0, skip: true };
        } else {
          info[rowIdx][col.key] = { rowSpan: 1, skip: false };
          prevKey = currentKey;
          startIdx = rowIdx;
        }
      });
    });

    return info;
  }, [data, columns]);

  // 2) mergeInfo 이후에 hover 헬퍼 선언
  const getHoverGroup = (rowIdx) => {
    if (!mergeInfo?.length) return rowIdx;

    const firstMergeCol = columns?.find((col) => col.mergeBy);
    if (!firstMergeCol) return rowIdx;

    const merge = mergeInfo[rowIdx]?.[firstMergeCol.key];

    if (merge?.skip) {
      for (let i = rowIdx - 1; i >= 0; i--) {
        const m = mergeInfo[i]?.[firstMergeCol.key];
        if (!m?.skip) return i;
      }
    }

    return rowIdx;
  };

  const isRowHovered = (rowIdx) => {
    if (hoveredRow === null) return false;
    return getHoverGroup(rowIdx) === getHoverGroup(hoveredRow);
  };

  if (!data || data.length < 1) {
    return (
      <Grid $columns={gridColumns}>
        {hasPrefix && <AdditionalTh>{prefixHeader}</AdditionalTh>}
        {columns?.map((col) => (
          <Th key={`th-${col.key}`}>
            <ThLabel>{t(col.label)}</ThLabel>
          </Th>
        ))}
        {hasSuffix && <AdditionalTh>{suffixHeader}</AdditionalTh>}
        <EmptyRow $span={totalCols}>
          {t(I18N_KEYS.common.error.noData)}
        </EmptyRow>
      </Grid>
    );
  }

  const getGridRowForData = (dataIdx) => 3 + dataIdx * 2;
  const hasMerge = mergeInfo.length > 0;
  const firstCol = columns?.[0];
  const lastCol = columns?.[columns.length - 1];

  return (
    <Grid $columns={gridColumns}>
      {hasPrefix && <AdditionalTh>{prefixHeader}</AdditionalTh>}
      {columns?.map((col) => (
        <Th
          key={`th-${col.key}`}
          style={{ cursor: col.compareBy ? "pointer" : "default" }}
          onClick={() => onSort?.(col)}>
          <ThLabel>
            {t(col.label)}
            {col.compareBy && (
              <IconWrapper
                size={"1rem"}
                style={{
                  position: "absolute",
                  right: "-1.25rem",
                  top: "50%",
                  transform: "translateY(-50%)",
                }}>
                <SortIcon
                  sortDirection={
                    sortKey === col.key ? (isAscending ? "asc" : "desc") : null
                  }
                />
              </IconWrapper>
            )}
          </ThLabel>
        </Th>
      ))}
      {hasSuffix && <AdditionalTh>{suffixHeader}</AdditionalTh>}

      <Divider $span={totalCols} />

      {data?.map((row, rowIdx) => {
        const gridRow = getGridRowForData(rowIdx);
        const isLastRow = rowIdx === data.length - 1;
        const hovered = onRowClick && isRowHovered(rowIdx);

        // 3) hoverProps 통일 — rowIdx 기반, 한 번만 정의
        const hoverProps = onRowClick
          ? {
              onMouseEnter: () => setHoveredRow(rowIdx),
              onMouseLeave: () => setHoveredRow(null),
              onClick: () => onRowClick(row),
              $hovered: hovered,
            }
          : {};

        return (
          <React.Fragment key={row.id ?? rowIdx}>
            {/* prefix */}
            {hasPrefix &&
              (() => {
                const firstMerge = firstCol
                  ? mergeInfo?.[rowIdx]?.[firstCol.key]
                  : null;
                if (firstMerge?.skip) return null;

                const rowSpan = firstMerge?.rowSpan ?? 1;
                const gridRowSpan = rowSpan * 2 - 1;
                const gridStyle = hasMerge
                  ? {
                      gridRow: `${gridRow} / span ${gridRowSpan}`,
                      gridColumn: 1,
                    }
                  : {};

                return (
                  <AdditionalTd {...hoverProps} style={gridStyle}>
                    {prefixBodyBuilder(row)}
                  </AdditionalTd>
                );
              })()}

            {/* columns */}
            {columns?.map((col, colIdx) => {
              const merge = mergeInfo?.[rowIdx]?.[col.key];
              if (merge?.skip) return null;

              const gridColumn = (hasPrefix ? 2 : 1) + colIdx;
              const rowSpan = merge?.rowSpan ?? 1;
              const gridRowSpan = rowSpan * 2 - 1;

              const isThisMerged = rowSpan > 1;
              const prevCol = colIdx > 0 ? columns[colIdx - 1] : null;
              const prevMerge = prevCol
                ? mergeInfo?.[rowIdx]?.[prevCol.key]
                : null;
              const isPrevMerged =
                (prevMerge?.rowSpan ?? 1) > 1 || prevMerge?.skip;

              const needsLeftBorder =
                (colIdx > 0 || hasPrefix) && (isThisMerged || isPrevMerged);

              const gridStyle = hasMerge
                ? { gridRow: `${gridRow} / span ${gridRowSpan}`, gridColumn }
                : {};

              const renderedContent = col.render
                ? col.render(row[col.key], row, renderProps)
                : row[col.key];

              return (
                <Td
                  key={`${row.id ?? rowIdx}-${col.key}`}
                  {...hoverProps}
                  style={{
                    ...(onRowClick ? { cursor: "pointer" } : {}),
                    ...gridStyle,
                    ...(needsLeftBorder
                      ? { borderLeft: `1px solid ${colors.LightGray}` }
                      : {}),
                  }}>
                  <TdContent $isEllipsis={col.isEllipsis}>
                    {renderedContent}
                  </TdContent>
                </Td>
              );
            })}

            {/* suffix */}
            {hasSuffix &&
              (() => {
                const lastMerge = lastCol
                  ? mergeInfo?.[rowIdx]?.[lastCol.key]
                  : null;
                if (lastMerge?.skip) return null;

                const rowSpan = lastMerge?.rowSpan ?? 1;
                const gridRowSpan = rowSpan * 2 - 1;
                const gridStyle = hasMerge
                  ? {
                      gridRow: `${gridRow} / span ${gridRowSpan}`,
                      gridColumn: totalCols,
                    }
                  : {};

                return (
                  <AdditionalTd {...hoverProps} style={gridStyle}>
                    {suffixBodyBuilder(row)}
                  </AdditionalTd>
                );
              })()}

            {!isLastRow && (
              <Divider
                $span={totalCols}
                style={hasMerge ? { gridRow: gridRow + 1 } : undefined}
              />
            )}
          </React.Fragment>
        );
      })}
    </Grid>
  );
};
